import React, { FC } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';

import styles from './index.module.scss';

export interface IntegrationCardProps {
    /** Display name of the integration */
    name: string;
    /** Path to logo image */
    logo: string;
    /** Whether the integration is currently configured */
    isConfigured: boolean;
    /** Whether the card is read-only (no click action) */
    isReadOnly?: boolean;
    /** Click handler for configuring the integration */
    onClick?: () => void;
}

/**
 * Lock with checkmark icon for configured read-only integrations.
 */
const LockCheckIcon: FC<{ className?: string }> = ({ className }) => (
    <svg
        className={className}
        width="1em"
        height="1em"
        viewBox="0 0 330 330"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path d="M65,330h200c8.284,0,15-6.716,15-15V145c0-8.284-6.716-15-15-15h-15V85c0-46.869-38.131-85-85-85S80,38.131,80,85v45H65c-8.284,0-15,6.716-15,15v170C50,323.284,56.716,330,65,330z M207.481,219.356l-42.5,42.5c-2.929,2.929-6.768,4.394-10.606,4.394s-7.678-1.465-10.606-4.394l-21.25-21.25c-5.858-5.858-5.858-15.354,0-21.213c5.857-5.858,15.355-5.858,21.213,0l10.644,10.643l31.894-31.893c5.857-5.858,15.355-5.858,21.213,0C213.34,204.002,213.34,213.498,207.481,219.356z M110,85c0-30.327,24.673-55,55-55s55,24.673,55,55v45H110V85z" />
    </svg>
);

/**
 * Card component for displaying an integration option.
 * Shows logo, name, and checkmark badge if configured.
 */
const IntegrationCard: FC<IntegrationCardProps> = ({
    name,
    logo,
    isConfigured,
    isReadOnly = false,
    onClick,
}) => {
    const handleClick = (): void => {
        if (!isReadOnly && onClick) {
            onClick();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };

    return (
        <div
            className={`${styles.integrationCard} ${isReadOnly ? styles.readOnly : ''} ${!isReadOnly ? styles.clickable : ''}`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role={isReadOnly ? 'presentation' : 'button'}
            tabIndex={isReadOnly ? -1 : 0}
            aria-label={`${name} integration${isConfigured ? ' (configured)' : ''}${isReadOnly ? ' (read-only)' : ''}`}
        >
            <div className={styles.logoContainer}>
                <img
                    src={logo}
                    alt={`${name} logo`}
                    className={styles.logo}
                />
                {isConfigured && (
                    isReadOnly ? (
                        <LockCheckIcon className={styles.lockCheckIcon} />
                    ) : (
                        <CheckCircleFilled className={styles.checkmark} />
                    )
                )}
            </div>
            <span className={styles.name}>{name}</span>
        </div>
    );
};

export default IntegrationCard;

